import React, {useEffect, useState} from 'react';
import {Stage} from '../Stage';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import {UploadFile} from "./UploadFile";
import {UploadStageFile} from "../../../api/application/getMyApplicationDetails";
import {useMutation} from "@apollo/client";
import {SUBMIT_UPLOAD_STAGE_FILES} from "../../../api/stage/submitUploadStageFiles";
import {ErrorMessage} from "../../common/ErrorMessage";
import {LoadingButton} from "@mui/lab";

type UploadStageProps = {
    description: string,
    disabled: boolean,
    files: UploadStageFile[],
    id: string,
    refetch: () => void,
    title: string,
}

export const UploadStage = (props: UploadStageProps) => {
    const {description, disabled, files, id, refetch, title} = props

    const [filesData, setFilesData] = useState<{id: string, fileId: string | undefined}[]>([])
    const [snackBarMessage, setSnackBarMessage] = useState('')

    const [submitUploadedFiles, {loading, error}] = useMutation(SUBMIT_UPLOAD_STAGE_FILES)

    const onUpload = (id: string, fileId: string | undefined) => {
        setFilesData(oldState => oldState.map(file => {
            if (file.id === id) return {id, fileId}
            else return file
        }))
    }

    const onSubmit = async () => {
        const {errors} = await submitUploadedFiles({variables: {input: filesData.map(fileData => ({
            fileId: fileData.fileId,
            applicationStageFileId: fileData.id,
        }))}})

        if (errors === undefined || errors.length === 0) {
            setSnackBarMessage('')
            refetch()
        }
    }

    useEffect(() => {
        setFilesData(files.map(file => ({
            fileId: undefined,
            id: file.id,
        })))
    }, [files])

    useEffect(() => {
        if (error) setSnackBarMessage(error.message)
    }, [error?.message])

    const isSubmitted = files && files.every(file => !!file.fileId)

    return (
        <Stage
            expanded={true}
            id={id}
            icon={<AccessTimeIcon color="warning"/>}
            title={title}
            subTitle={isSubmitted ? 'The team has been notified of your upload, please be patient while we review it.' : 'Not yet completed'}
        >
            <Typography marginBottom="0.5rem">
                {description}
            </Typography>

            <ul>
                {files.map((fileData) => {
                    return (
                        <li key={fileData.id}>
                            <UploadFile disabled={disabled} fileData={fileData} onUpload={(fileId) => onUpload(fileData.id, fileId)} />
                        </li>
                    )
                })}
            </ul>

            {isSubmitted ? undefined : (
                <Stack direction="row" gap="1rem" justifyContent="flex-end" marginTop="1rem">
                    <LoadingButton
                        color="success"
                        disabled={filesData.length === 0 || filesData.some(file => file.fileId === undefined) || disabled}
                        loading={loading}
                        onClick={onSubmit}
                        variant="contained"
                    >
                        Submit
                    </LoadingButton>
                </Stack>
            )}

            <ErrorMessage
                handleClose={() => setSnackBarMessage('')}
                isOpen={snackBarMessage !== ''}
                message={snackBarMessage}
            />
        </Stage>
    )
}
